/*!
 * Trobart hero (landing page and 100 Cims)
 * A slow, close-up camera that drifts over the real artwork, with a touch of depth of field,
 * soft window light and paper tooth. Random maps, focus-pull transitions, drag / pinch to explore.
 * Maps can come straight from the shop's products (images resized by Shopify, layout worked out here).
 * No dependencies. WebGL2 with a CSS fallback.
 */
(function () {
  'use strict';

  var DEG = Math.PI / 180;
  var PRINT_H = 40;                  // the plane is 40 units tall (≈ cm of a 30x40 print)
  var BASE_APERTURE = 0.75;          // lens aperture in plane units: sets how shallow the focus is
  var AZ_MAX = 10;                   // the camera turns at most this much from straight on (degrees)
  var IDLE_RESUME_S = 6;             // the autopilot takes over again after this much idle time

  function clamp(x, a, b) { return x < a ? a : x > b ? b : x; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function smooth(t) { t = clamp(t, 0, 1); return t * t * t * (t * (t * 6 - 15) + 10); }
  function ease(t) { return 0.3 * clamp(t, 0, 1) + 0.7 * smooth(t); }
  function cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
  function norm(a) { var l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }
  function rng32(seed) {
    var s = seed >>> 0 || 1;
    return function () { s ^= s << 13; s >>>= 0; s ^= s >> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
  }
  // critically damped spring (implicit, stable for any dt)
  function spring(x, v, target, omega, dt) {
    var f = 1 + 2 * dt * omega, oo = omega * omega, hoo = dt * oo, hhoo = dt * hoo;
    var inv = 1 / (f + hhoo);
    return [(f * x + dt * v + hhoo * target) * inv, (v + hoo * (target - x)) * inv];
  }
  var KEYS = ['u', 'v', 'ld', 'tilt', 'az', 'fov'];
  // A longish lens: the shots are written for a 40° view, then taken from farther away with a narrower one,
  // so the framing stays the same but the sheet keeps its shape (less perspective, close to a top view).
  var LENS = 0.62;                   // view angle relative to 40°
  function pose(u, v, d, tilt, az, fov) {
    var f0 = fov || 40, f = f0 * LENS;
    return { u: u, v: v, ld: Math.log(d * Math.tan(f0 * DEG / 2) / Math.tan(f * DEG / 2)), tilt: tilt, az: az, fov: f };
  }
  var DSCALE = Math.tan(20 * DEG) / Math.tan(20 * LENS * DEG);     // distance factor for the same framing
  function copyPose(p) { return { u: p.u, v: p.v, ld: p.ld, tilt: p.tilt, az: p.az, fov: p.fov }; }
  // a sideways angle within ±AZ_MAX, most often close to straight on
  function azPick(r, scale) { return AZ_MAX * (scale || 1) * (r() + r() + r() - 1.5) / 1.5; }

  function camera(p, S, roll) {
    var tx = p.u * S[0], ty = p.v * S[1], d = Math.exp(p.ld);
    var tl = p.tilt * DEG, az = p.az * DEG, dx = Math.sin(az), dy = Math.cos(az);
    var C = [tx + d * Math.sin(tl) * dx, ty + d * Math.sin(tl) * dy, d * Math.cos(tl)];
    var F = norm([tx - C[0], ty - C[1], -C[2]]);
    var R = norm(cross([0, 0, 1], F));
    var U = cross(F, R);
    if (roll) {
      var c = Math.cos(roll * DEG), s = Math.sin(roll * DEG);
      var R2 = [R[0] * c + U[0] * s, R[1] * c + U[1] * s, R[2] * c + U[2] * s];
      U = [U[0] * c - R[0] * s, U[1] * c - R[1] * s, U[2] * c - R[2] * s]; R = R2;
    }
    return { C: C, F: F, R: R, U: U, tanY: Math.tan(p.fov * DEG / 2), dir: [dx, dy], d: d };
  }
  // where a screen point (NDC) lands on the print plane
  function hit(cam, nx, ny, asp) {
    var D = [cam.F[0] + nx * cam.tanY * asp * cam.R[0] + ny * cam.tanY * cam.U[0],
             cam.F[1] + nx * cam.tanY * asp * cam.R[1] + ny * cam.tanY * cam.U[1],
             cam.F[2] + nx * cam.tanY * asp * cam.R[2] + ny * cam.tanY * cam.U[2]];
    var t = D[2] < -1e-3 ? -cam.C[2] / D[2] : 400;
    t = Math.min(t, 400);
    return { x: cam.C[0] + t * D[0], y: cam.C[1] + t * D[1], t: t };
  }

  // ------------------------------------------------------------------ shaders
  var VS = '#version 300 es\nin vec2 p;out vec2 vN;void main(){vN=p;gl_Position=vec4(p,0.,1.);}';
  var FS = [
    '#version 300 es',
    'precision highp float;',
    'in vec2 vN; out vec4 outColor;',
    'uniform vec2 uRes; uniform float uTime, uMix, uExpo; uniform int uTaps;',
    'uniform sampler2D uTex0, uTex1; uniform vec2 uSize0, uSize1;',
    'uniform vec3 uC0, uF0, uR0, uU0, uC1, uF1, uR1, uU1;',
    'uniform vec4 uL0, uL1;',           // tanY, aspect, focus distance, aperture
    'uniform vec3 uDesk;',
    'float h21(vec2 p){vec3 q=fract(vec3(p.xyx)*.1031);q+=dot(q,q.yzx+33.33);return fract((q.x+q.y)*q.z);}',
    'float vn(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*(3.-2.*f);',
    ' return mix(mix(h21(i),h21(i+vec2(1,0)),u.x),mix(h21(i+vec2(0,1)),h21(i+vec2(1,1)),u.x),u.y);}',
    'vec3 desk(vec2 P, vec2 S){',
    ' float n=vn(P*.07)*.6+vn(P*.33)*.3+vn(P*1.9)*.1;',
    ' vec3 c=uDesk*(.8+.4*n);',
    ' vec2 q=P-vec2(.45,.65); vec2 o=max(max(-q,q-S),0.);',
    ' float inside=step(0.,q.x)*step(0.,q.y)*step(q.x,S.x)*step(q.y,S.y);',
    ' return c*mix(1.-.6*exp(-length(o)/1.4),.4,inside);}',
    'vec3 tap(sampler2D tx, vec2 P, vec2 S, vec2 gx, vec2 gy, float aa){',
    ' vec3 c=textureGrad(tx,P/S,gx,gy).rgb; c*=c;',
    ' vec2 e=min(P,S-P); float m=clamp(min(e.x,e.y)/aa+.5,0.,1.);',
    ' if(m>=1.) return c;',
    ' return mix(desk(P,S),c,m);}',
    'vec3 shade(sampler2D tx, vec2 S, vec3 C, vec3 F, vec3 R, vec3 U, vec4 L){',
    ' vec3 D=F+vN.x*L.x*L.y*R+vN.y*L.x*U;',
    ' float dz=min(D.z,-1e-4); float t=-C.z/dz;',
    ' vec2 P=C.xy+t*D.xy;',
    ' vec2 dPx=dFdx(P), dPy=dFdy(P);',
    ' float aa=max(length(dPx),length(dPy))+1e-5;',
    ' vec2 gx=dPx/S, gy=dPy/S;',
    ' float fpx=uRes.y*.5/L.x;',
    ' float r=min(.5*L.w*fpx*abs(t-L.z)/(t*L.z),30.);',
    ' vec3 col;',
    ' if(r<.6){ col=tap(tx,P,S,gx,gy,aa); }',
    ' else {',
    '  float g=max(1.,r*.5); float a0=h21(gl_FragCoord.xy+fract(uTime*7.13)*97.)*6.2832;',
    '  vec3 acc=vec3(0.); float n=float(uTaps);',
    '  for(int i=0;i<16;i++){ if(i>=uTaps) break;',
    '   float fi=float(i)+.5; float rr=sqrt(fi/n)*r; float th=fi*2.39996+a0;',
    '   vec2 o=vec2(cos(th),sin(th))*rr;',
    '   acc+=tap(tx,P+dPx*o.x+dPy*o.y,S,gx*g,gy*g,aa*g); }',
    '  col=acc/n; }',
    ' vec3 Lp=vec3(-.45*S.x,-.35*S.y,70.); vec3 Ld=normalize(Lp-vec3(P,0.));',
    ' float diff=.6+.46*Ld.z;',
    ' float fall=1.-.15*clamp(length(P/S-vec2(.18,.08)),0.,1.3);',
    ' vec3 V=normalize(C-vec3(P,0.)); vec3 H=normalize(Ld+V);',
    ' float spec=pow(max(H.z,0.),18.)*.055;',
    ' float fib=(vn(P*vec2(34.,6.))-.5)*.6+(vn(P*27.)-.5)*.4;',
    ' col*=1.+.05*fib*clamp(1.-aa*55.,0.,1.);',
    ' vec3 lit=col*diff*fall+spec*vec3(1.,.97,.92);',
    ' return mix(lit,uDesk*.93,step(-1e-4,D.z));}',
    'void main(){',
    ' vec3 c;',
    ' if(uMix<=.001) c=shade(uTex0,uSize0,uC0,uF0,uR0,uU0,uL0);',
    ' else if(uMix>=.999) c=shade(uTex1,uSize1,uC1,uF1,uR1,uU1,uL1);',
    ' else c=mix(shade(uTex0,uSize0,uC0,uF0,uR0,uU0,uL0),shade(uTex1,uSize1,uC1,uF1,uR1,uU1,uL1),uMix);',
    ' vec2 q=vN*vec2(uRes.x/uRes.y,1.);',
    ' c*=1.-.11*dot(q,q);',
    ' c*=uExpo; c=c/(1.+.06*c); c=sqrt(max(c,0.));',
    ' c*=vec3(1.012,1.,.982);',
    ' c+=(h21(gl_FragCoord.xy*1.37+fract(uTime*.37)*113.)-.5)*.018;',
    ' outColor=vec4(c,1.);}'
  ].join('\n');

  // ------------------------------------------------------------------ layout of a map, worked out in the browser
  // Where the artwork sits on the sheet, its most detailed places (for the camera) and the printed title.
  // Self-contained (no closures) so it can also run in a Worker.  px: RGBA bytes, W×H with the long side ≈ 1000 px.
  function tbLayout(px, W, H) {
    var N = W * H, i, x, y, k;
    var s = Math.max(W, H) / 1000;                      // tuned at 1000 px on the long side
    var L = new Float32Array(N), A = new Float32Array(N), Bb = new Float32Array(N), G = new Float32Array(N);
    var lin = new Float32Array(256);
    for (i = 0; i < 256; i++) { var c0 = i / 255; lin[i] = c0 <= 0.04045 ? c0 / 12.92 : Math.pow((c0 + 0.055) / 1.055, 2.4); }
    var fl = function (t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; };
    for (i = 0; i < N; i++) {
      var r = px[4 * i], g = px[4 * i + 1], b = px[4 * i + 2];
      G[i] = 0.299 * r + 0.587 * g + 0.114 * b;
      var R = lin[r], Gl = lin[g], Bl = lin[b];
      var X = (0.412453 * R + 0.357580 * Gl + 0.180423 * Bl) / 0.950456;
      var Y = 0.212671 * R + 0.715160 * Gl + 0.072169 * Bl;
      var Z = (0.019334 * R + 0.119193 * Gl + 0.950227 * Bl) / 1.088754;
      var fx = fl(X), fy = fl(Y), fz = fl(Z);
      L[i] = (Y > 0.008856 ? 116 * fy - 16 : 903.3 * Y) * 2.55;     // OpenCV's 8-bit Lab scale
      A[i] = 500 * (fx - fy) + 128; Bb[i] = 200 * (fy - fz) + 128;
    }
    // box mean over a (2r+1)² window, edges clamped to the image
    function boxBlur(src, rad, w, h) {
      w = w || W; h = h || H;
      var n = w * h, tmp = new Float32Array(n), out = new Float32Array(n), xx, yy, acc, cnt, o;
      for (yy = 0; yy < h; yy++) {                       // rows
        o = yy * w; acc = 0; cnt = 0;
        for (xx = 0; xx <= Math.min(rad, w - 1); xx++) { acc += src[o + xx]; cnt++; }
        for (xx = 0; xx < w; xx++) {
          tmp[o + xx] = acc / cnt;
          var add = xx + rad + 1, sub = xx - rad;
          if (add < w) { acc += src[o + add]; cnt++; }
          if (sub >= 0) { acc -= src[o + sub]; cnt--; }
        }
      }
      var col = new Float32Array(w); cnt = 0;            // columns, walked row by row (cache friendly)
      for (yy = 0; yy <= Math.min(rad, h - 1); yy++) { o = yy * w; for (xx = 0; xx < w; xx++) col[xx] += tmp[o + xx]; cnt++; }
      for (yy = 0; yy < h; yy++) {
        o = yy * w; var inv = 1 / cnt;
        for (xx = 0; xx < w; xx++) out[o + xx] = col[xx] * inv;
        var a2 = yy + rad + 1, s2 = yy - rad;
        if (a2 < h) { o = a2 * w; for (xx = 0; xx < w; xx++) col[xx] += tmp[o + xx]; cnt++; }
        if (s2 >= 0) { o = s2 * w; for (xx = 0; xx < w; xx++) col[xx] -= tmp[o + xx]; cnt--; }
      }
      return out;
    }
    function gauss(src, sigma, w, h) {                  // three box passes ≈ a Gaussian
      var wi = Math.sqrt(12 * sigma * sigma / 3 + 1), wl = Math.floor(wi); if (wl % 2 === 0) wl--;
      var m = Math.round((12 * sigma * sigma - 3 * wl * wl - 12 * wl - 9) / (-4 * wl - 4)), out = src;
      for (var p = 0; p < 3; p++) out = boxBlur(out, ((p < m ? wl : wl + 2) - 1) / 2, w, h);
      return out;
    }
    function morph(mask, rad, dil) {                    // square max (dilate) or min (erode) filter
      var tmp = new Uint8Array(N), out = new Uint8Array(N), xx, yy, want = dil ? 1 : 0;
      var prev = new Int32Array(W), next = new Int32Array(W), pv = new Int32Array(H);
      for (yy = 0; yy < H; yy++) {
        var o = yy * W, last = -1e9;                     // distance-based: nearest set (dilate) / unset (erode) pixel
        for (xx = 0; xx < W; xx++) { if (mask[o + xx] === want) last = xx; prev[xx] = last; }
        last = 1e9;
        for (xx = W - 1; xx >= 0; xx--) { if (mask[o + xx] === want) last = xx; next[xx] = last; }
        for (xx = 0; xx < W; xx++) {
          var near = (xx - prev[xx] <= rad) || (next[xx] - xx <= rad);
          tmp[o + xx] = dil ? (near ? 1 : 0) : (near ? 0 : 1);
        }
      }
      for (xx = 0; xx < W; xx++) {
        var want2 = want, lst = -1e9;
        for (yy = 0; yy < H; yy++) { if (tmp[yy * W + xx] === want2) lst = yy; pv[yy] = lst; }
        lst = 1e9;
        for (yy = H - 1; yy >= 0; yy--) {
          if (tmp[yy * W + xx] === want2) lst = yy;
          var nr = (yy - pv[yy] <= rad) || (lst - yy <= rad);
          out[yy * W + xx] = dil ? (nr ? 1 : 0) : (nr ? 0 : 1);
        }
      }
      return out;
    }
    function components(mask) {                         // 8-connected labelling with area, box and centroid
      var lab = new Int32Array(N), st = [], stack = new Int32Array(N), j, n = 0;
      for (j = 0; j < N; j++) {
        if (!mask[j] || lab[j]) continue;
        n++; var top = 0, area = 0, x0 = 1e9, y0 = 1e9, x1 = -1, y1 = -1, sx = 0, sy = 0;
        stack[top++] = j; lab[j] = n;
        while (top) {
          var q = stack[--top], qx = q % W, qy = (q - qx) / W;
          area++; sx += qx; sy += qy;
          if (qx < x0) x0 = qx; if (qx > x1) x1 = qx; if (qy < y0) y0 = qy; if (qy > y1) y1 = qy;
          for (var dy = -1; dy <= 1; dy++) {
            var ny = qy + dy; if (ny < 0 || ny >= H) continue;
            for (var dx = -1; dx <= 1; dx++) {
              var nx = qx + dx; if (nx < 0 || nx >= W) continue;
              var t = ny * W + nx; if (mask[t] && !lab[t]) { lab[t] = n; stack[top++] = t; }
            }
          }
        }
        st.push({ x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1, area: area, cx: sx / area, cy: sy / area });
      }
      return { lab: lab, st: st };
    }
    function median(a) { var b = Float64Array.from(a).sort(), m = b.length >> 1; return b.length % 2 ? b[m] : (b[m - 1] + b[m]) / 2; }

    // paper colour from a 6 px ring around the sheet
    var bd = Math.max(2, Math.round(6 * s)), ring = [];
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) if (y < bd || y >= H - bd || x < bd || x >= W - bd) ring.push(y * W + x);
    var bgL = median(ring.map(function (j) { return L[j]; })), bgA = median(ring.map(function (j) { return A[j]; })),
        bgB = median(ring.map(function (j) { return Bb[j]; }));
    var flat = median(ring.map(function (j) { return Math.abs(L[j] - bgL) + Math.abs(A[j] - bgA) + Math.abs(Bb[j] - bgB); })) < 6;
    var diff = new Float32Array(N);
    for (i = 0; i < N; i++) diff[i] = Math.abs(L[i] - bgL) + Math.abs(A[i] - bgA) + Math.abs(Bb[i] - bgB);
    // local texture: standard deviation of grey in 7×7, softened
    var G2 = new Float32Array(N); for (i = 0; i < N; i++) G2[i] = G[i] * G[i];
    var r3 = Math.max(1, Math.round(3 * s)), mu = boxBlur(G, r3), m2 = boxBlur(G2, r3), sd = new Float32Array(N);
    for (i = 0; i < N; i++) sd[i] = Math.sqrt(Math.max(m2[i] - mu[i] * mu[i], 0));
    var tex = gauss(sd, 6 * s);
    var art = new Uint8Array(N);
    if (flat) {
      for (i = 0; i < N; i++) art[i] = diff[i] > 18 && tex[i] > 2.5 ? 1 : 0;
      art = morph(morph(art, Math.round(7 * s), true), Math.round(7 * s), false);     // close 15×15
      art = morph(morph(art, Math.round(4 * s), false), Math.round(4 * s), true);     // open 9×9
      var cc = components(art), big = 0;
      cc.st.forEach(function (c) { if (c.area > big) big = c.area; });
      var keepL = cc.st.map(function (c) { return c.area > 0.04 * big; }), any = false;
      for (i = 0; i < N; i++) { var lb = cc.lab[i]; art[i] = lb && keepL[lb - 1] ? 1 : 0; if (art[i]) any = true; }
      if (!any) art.fill(1);
    } else art.fill(1);
    var bx0 = W, by0 = H, bx1 = 0, by1 = 0;
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) if (art[y * W + x]) {
      if (x < bx0) bx0 = x; if (x > bx1) bx1 = x; if (y < by0) by0 = y; if (y > by1) by1 = y;
    }
    var box = [bx0 / W, by0 / H, (bx1 + 1) / W, (by1 + 1) / H];
    // points of interest: the most detailed places inside the artwork
    var f4 = 4, w4 = Math.ceil(W / f4), h4 = Math.ceil(H / f4), e = new Float32Array(w4 * h4), cnt4 = new Float32Array(w4 * h4);
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) { var q4 = ((y / f4) | 0) * w4 + ((x / f4) | 0); e[q4] += tex[y * W + x] * art[y * W + x]; cnt4[q4]++; }
    for (i = 0; i < w4 * h4; i++) e[i] /= cnt4[i] || 1;
    e = gauss(e, 18 * s / f4, w4, h4);
    var poi = [], rad = 0.13 * Math.max(W, H) / f4;
    for (k = 0; k < 7; k++) {
      var bi = 0, bv = 0;
      for (i = 0; i < w4 * h4; i++) if (e[i] > bv) { bv = e[i]; bi = i; }
      if (bv <= 0) break;
      var px0 = bi % w4, py0 = (bi - px0) / w4;
      poi.push([+(Math.min(1, (px0 + 0.5) * f4 / W)).toFixed(4), +(Math.min(1, (py0 + 0.5) * f4 / H)).toFixed(4)]);
      for (y = Math.max(0, Math.floor(py0 - rad)); y <= Math.min(h4 - 1, Math.ceil(py0 + rad)); y++)
        for (x = Math.max(0, Math.floor(px0 - rad)); x <= Math.min(w4 - 1, Math.ceil(px0 + rad)); x++)
          if ((x - px0) * (x - px0) + (y - py0) * (y - py0) <= rad * rad) e[y * w4 + x] = 0;
    }
    if (!poi.length) poi = [[0.5, 0.5]];

    // printed title: a centred line of letters on the plain paper, outside the artwork
    function findTitle() {
      var ink = new Uint8Array(N); for (var j = 0; j < N; j++) ink[j] = diff[j] > 22 ? 1 : 0;
      var cc2 = components(ink), sat = new Int32Array((W + 1) * (H + 1)), xx, yy;
      var bigSet = cc2.st.map(function (c) { return c.area > 0.002 * H * W; });
      for (yy = 0; yy < H; yy++) {
        var rowAcc = 0;
        for (xx = 0; xx < W; xx++) {
          var lb2 = cc2.lab[yy * W + xx]; rowAcc += lb2 && bigSet[lb2 - 1] ? 1 : 0;
          sat[(yy + 1) * (W + 1) + xx + 1] = sat[yy * (W + 1) + xx + 1] + rowAcc;
        }
      }
      var dr = Math.max(1, Math.round(4 * s));
      var nearBig = function (cx, cy) {
        var xa = Math.max(0, cx - dr), xb = Math.min(W, cx + dr + 1), ya = Math.max(0, cy - dr), yb = Math.min(H, cy + dr + 1);
        return sat[yb * (W + 1) + xb] - sat[ya * (W + 1) + xb] - sat[yb * (W + 1) + xa] + sat[ya * (W + 1) + xa] > 0;
      };
      var blobs = [];
      cc2.st.forEach(function (c) {
        if (nearBig(Math.floor(c.cx), Math.floor(c.cy))) return;
        if (c.h >= 4 * s && c.h <= 0.09 * H && c.w <= 0.1 * W && c.area >= 6 * s * s) blobs.push(c);
      });
      if (blobs.length < 4) return null;
      blobs.sort(function (p, q) { return (p.y + p.h / 2) - (q.y + q.h / 2); });
      var lines = [], cur = [blobs[0]];
      var meanCy = function (a) { var t = 0; a.forEach(function (b) { t += b.y + b.h / 2; }); return t / a.length; };
      var medH = function (a) { return median(a.map(function (b) { return b.h; })); };
      for (var bI = 1; bI < blobs.length; bI++) {
        var bb = blobs[bI];
        if (Math.abs(bb.y + bb.h / 2 - meanCy(cur)) < Math.max(3 * s, 0.4 * medH(cur))) cur.push(bb);
        else { lines.push(cur); cur = [bb]; }
      }
      lines.push(cur);
      var best = null;
      lines.forEach(function (Ln) {
        if (Ln.length < 4) return;
        Ln = Ln.slice().sort(function (p, q) { return p.cx - q.cx; });
        var runs = [], run = [Ln[0]];
        for (var q = 1; q < Ln.length; q++) {
          var pr = run[run.length - 1];
          if (Ln[q].x - (pr.x + pr.w) < 3.5 * medH(run)) run.push(Ln[q]); else { runs.push(run); run = [Ln[q]]; }
        }
        runs.push(run);
        runs.forEach(function (Rn) {
          if (Rn.length < 4) return;
          var x0 = 1e9, x1 = -1, y0 = 1e9, y1 = -1;
          Rn.forEach(function (b) { x0 = Math.min(x0, b.x); x1 = Math.max(x1, b.x + b.w); y0 = Math.min(y0, b.y); y1 = Math.max(y1, b.y + b.h); });
          var cx = (x0 + x1) / 2 / W, score = Rn.length * medH(Rn) * (Math.abs(cx - 0.5) < 0.12 ? 1.6 : 1);
          if (!best || score > best.score) best = { score: score, cx: cx, y0: y0 / H, y1: y1 / H, w: (x1 - x0) / W, n: Rn.length };
        });
      });
      if (!best || best.n < 4 || !(best.w > 0.02 && best.w < 0.7)) return null;
      return [+best.cx.toFixed(4), +best.y0.toFixed(4), +best.y1.toFixed(4), +best.w.toFixed(4)];
    }
    // or a small, plain, light label box holding the map's name
    function findCartouche() {
      var r2 = Math.max(1, Math.round(2 * s)), m1 = boxBlur(L, r2), L2 = new Float32Array(N), j;
      for (j = 0; j < N; j++) L2[j] = L[j] * L[j];
      var m2b = boxBlur(L2, r2), plain = new Uint8Array(N);
      for (j = 0; j < N; j++) plain[j] = Math.sqrt(Math.max(m2b[j] - m1[j] * m1[j], 0)) < 3 && L[j] > 200 ? 1 : 0;
      plain = morph(morph(plain, Math.round(3 * s), true), Math.round(3 * s), false);   // close 7×7
      var cc3 = components(plain), best = null, e3 = Math.round(3 * s);
      cc3.st.forEach(function (c, ci) {
        var xx, yy, w = c.w, h = c.h;
        if (!(0.05 * W < w && w < 0.4 * W && 0.025 * H < h && h < 0.2 * H)) return;
        if (!(1.3 < w / h && w / h < 7)) return;
        if (c.x < e3 || c.y < e3 || c.x + w > W - e3 || c.y + h > H - e3) return;
        // filled area: the box minus what can be reached from its edge without crossing the label
        var id = ci + 1, seen = new Uint8Array(w * h), st2 = [], outside = 0;
        var push = function (ax, ay) { var t = ay * w + ax; if (seen[t]) return; if (cc3.lab[(c.y + ay) * W + c.x + ax] === id) return; seen[t] = 1; st2.push(t); };
        for (xx = 0; xx < w; xx++) { push(xx, 0); push(xx, h - 1); }
        for (yy = 0; yy < h; yy++) { push(0, yy); push(w - 1, yy); }
        while (st2.length) {
          var t = st2.pop(), tx = t % w, ty = (t - tx) / w; outside++;
          if (tx > 0) push(tx - 1, ty); if (tx < w - 1) push(tx + 1, ty);
          if (ty > 0) push(tx, ty - 1); if (ty < h - 1) push(tx, ty + 1);
        }
        if (w * h - outside < 0.85 * w * h) return;
        var inkN = 0, tot = 0;
        for (yy = c.y + e3; yy < c.y + h - e3; yy++) for (xx = c.x + e3; xx < c.x + w - e3; xx++) { tot++; if (L[yy * W + xx] < 150) inkN++; }
        var ink = tot ? inkN / tot : 0;
        if (ink < 0.003) return;
        var edge = Math.min(c.x, c.y, W - c.x - w, H - c.y - h) / Math.max(W, H);
        var score = Math.min(ink, 0.08) * (edge < 0.12 ? 1.5 : 1) * w;
        if (!best || score > best.score) best = { score: score, cx: (c.x + w / 2) / W, y0: c.y / H, y1: (c.y + h) / H, w: w / W };
      });
      return best ? [+best.cx.toFixed(4), +best.y0.toFixed(4), +best.y1.toFixed(4), +best.w.toFixed(4)] : null;
    }
    var title = flat ? findTitle() : null;
    if (title && !(title[1] > 0.62 || title[2] < 0.14)) title = null;
    if (!title) title = findCartouche();
    return { box: box.map(function (v) { return +v.toFixed(4); }), poi: poi, tit: title, fb: !flat };
  }
  // product titles: "ELS ALPS. Mapa de relleu artístic / paleta clàssica" -> name, kind, style
  function parseTitle(t) {
    t = String(t || '').trim();
    var name = t, rest = '', m = /^(.{2,60}?)\.\s+(\S.*)$/.exec(t);
    if (m) { name = m[1]; rest = m[2]; }
    else { var p = t.split(/\s[-\u2013|]\s/); if (p.length > 1) { name = p[0]; rest = p.slice(1).join(' · '); } }
    var k = rest.split(/\s\/\s/);
    return { name: name, kind: k[0] || '', style: k.length > 1 ? k.slice(1).join(' / ') : '' };
  }
  // Shopify image URLs take a width; other URLs are used as they are
  function sized(url, w) {
    if (!/\/cdn\/shop\/|cdn\.shopify\.com/.test(url)) return url;
    var h = url.split('#')[0], q = h.indexOf('?'), base = q < 0 ? h : h.slice(0, q), qs = q < 0 ? '' : h.slice(q + 1);
    var parts = qs.split('&').filter(function (kv) { return kv && !/^(width|height|crop)=/.test(kv); });
    parts.push('width=' + Math.max(16, Math.round(w)));
    return base + '?' + parts.join('&');
  }

  // ------------------------------------------------------------------ one hero
  function init(root) {
    root.classList.remove('tb-nojs');
    root.dataset.tbInitialized = 'true';
    var ES = root.getAttribute('data-locale') !== 'ca';
    function txt(ca, es) { return ES ? es : ca; }
    var $ = function (s) { return root.querySelector(s); };
    var DATA;
    try { DATA = JSON.parse($('.tb-data').textContent); } catch (e) { DATA = null; }
    if (!DATA || !DATA.length) {                  // nothing to show: say so in the theme editor, hide on the shop
      var msg = root.getAttribute('data-empty'), lo = $('.tb-loading');
      if (msg && lo) lo.textContent = msg; else root.hidden = true;
      return;
    }
    // maps that come straight from the shop: { id, t: title, u: url, a: w/h, w, h, img, q: [[url, w, h], …], st }
    function normalizeMap(c) {
      if (!c.t || c.name) return c;
      var p = parseTitle(c.t);
      c.dyn = true; c.name = p.name; c.kind = p.kind || 'Mapa'; c.style = c.st || p.style || '';
      if (c.u) c.url = c.u;
      return c;
    }
    DATA.forEach(normalizeMap);
    var BASE = root.getAttribute('data-base') || '';
    var SITE = root.getAttribute('data-site') || '';
    var WHEEL_FREE = root.getAttribute('data-wheel') === 'free';
    var SINGLE_TOUCH = root.classList.contains('tb-hero--viewer');
    var reduceMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    var coarse = window.matchMedia && matchMedia('(pointer: coarse)').matches;

    var canvas = $('.tb-gl'), fallback = $('.tb-fallback');
    var thumb = $('.tb-thumb canvas'), tctx = thumb.getContext('2d');
    var card = $('.tb-card'), elNum = $('.tb-num'), elEss = $('.tb-ess'), elName = $('.tb-name'),
        elMeta = $('.tb-meta'), elTag = $('.tb-tag'), elLink = $('.tb-link');
    var bar = $('.tb-progress span'), barWrap = $('.tb-progress'), hint = $('.tb-hint'), loading = $('.tb-loading');
    var btnPause = $('.tb-pause');
    if (!WHEEL_FREE) {
      var m = hint.querySelector('.tb-mouse');
      if (m) m.textContent = txt('Arrossega per explorar · Pessiga o Ctrl + roda per apropar-te', 'Arrastra para explorar · Pellizca o Ctrl + rueda para acercarte');
    }

    // ---------------------------------------------------------------- order of cims
    var seed = (Date.now() ^ (Math.random() * 1e9)) >>> 0;
    var rand = rng32(seed);
    function shuffled() {
      var a = DATA.map(function (_, i) { return i; });
      for (var i = a.length - 1; i > 0; i--) { var j = Math.floor(rand() * (i + 1)); var t = a[i]; a[i] = a[j]; a[j] = t; }
      return a;
    }
    var order = shuffled(), pos = 0;
    var startId = root.getAttribute('data-start-id');
    if (startId) {
      var startIndex = DATA.findIndex(function (c) { return String(c.id) === startId; });
      if (startIndex >= 0) { order.splice(order.indexOf(startIndex), 1); order.unshift(startIndex); }
    }
    function peekNext() { return order[(pos + 1) % order.length]; }
    function stepOrder(dir) {
      pos += dir;
      if (pos >= order.length) { var last = order[order.length - 1]; order = shuffled(); if (order[0] === last) order.push(order.shift()); pos = 0; }
      if (pos < 0) pos = order.length - 1;
      return order[pos];
    }
    function randomPick() {
      var cur = order[pos], idx;
      do { idx = Math.floor(rand() * DATA.length); } while (idx === cur && DATA.length > 1);
      order.splice(pos + 1, 0, idx);
      // drop the later duplicate so the shuffle stays a permutation
      for (var k = order.length - 1; k > pos + 1; k--) if (order[k] === idx) { order.splice(k, 1); break; }
      pos += 1; return idx;
    }

    // ---------------------------------------------------------------- images
    var gl = null, maxTex = 4096;
    try {
      gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: false, stencil: false,
        premultipliedAlpha: false, powerPreference: 'high-performance' });
    } catch (e) { gl = null; }
    if (gl) maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    var saveData = navigator.connection && navigator.connection.saveData;
    var mem = navigator.deviceMemory || 8;
    // CSS screen width and touch input do not describe a high-DPI phone's image needs.
    var HI = (maxTex >= 4096 && !saveData && mem >= 4) ? 'lg' : 'md';

    var cache = {};   // id -> { sm: Promise<ImageBitmap|Image>, hi: Promise, prep: Promise }
    function loadImage(url) {
      if (window.createImageBitmap && window.fetch) {
        var opt = { credentials: 'same-origin' };
        if (/\/cdn\/shop\/|cdn\.shopify\.com/.test(url)) opt = { mode: 'cors', credentials: 'omit', headers: { Accept: 'image/webp,image/*;q=0.8' } };
        return fetch(url, opt).then(function (r) {
          if (!r.ok) throw new Error(r.status + ' ' + url);
          return r.blob();
        }).then(function (b) { return createImageBitmap(b); });
      }
      return new Promise(function (res, rej) {
        var im = new Image(); im.crossOrigin = 'anonymous'; im.decoding = 'async';
        im.onload = function () { res(im); }; im.onerror = rej; im.src = url;
      });
    }
    var LONG_SM = 1000;   // shop maps: the image that is analysed, shown first and used as the thumbnail
    function longW(c, L) { return c.a >= 1 ? L : L * c.a; }          // width that gives a long side of L
    function assets(i) {
      var c = DATA[i], e = cache[c.id];
      if (!e) {
        e = cache[c.id] = {};
        e.sm = loadImage(c.dyn ? sized(c.img, longW(c, LONG_SM)) : BASE + c.img.sm);
        e.sm.then(function (img) {
          e.smImg = img;
          if (thumbCim === i && DATA[i].id === c.id) { thumbImg = img; if (!gl) drawThumb(null); }
        }).catch(function () {});
      }
      return e;
    }
    function hiImage(i) {
      var e = assets(i), c = DATA[i];
      if (!e.hi) e.hi = c.dyn ? shopHi(c) : loadImage(BASE + c_img(i));
      return e.hi;
    }
    function c_img(i) { var im = DATA[i].img; return im[HI] || im.md || im.lg; }

    // ---------------------------------------------------------------- maps from the shop
    var TEX_LONG = Math.min(HI === 'lg' ? 4096 : 2048, maxTex);
    function longOf(w, h) { return Math.max(w || 0, h || 0); }
    // the full-size texture: the four detail images stitched together when they are the sheet's quarters, else the main image
    function shopHi(c) {
      if (c.hi) return loadImage(c.hi);                // local preview: the full-size file as it is
      if (c.quads) {
        var T = Math.min(TEX_LONG, 2 * longOf(c.quads[0][1], c.quads[0][2]));
        var W = Math.round(longW(c, T)), H = Math.round(W / c.a), xs = Math.round(W / 2), ys = Math.round(H / 2);
        return Promise.all(c.quads.map(function (q) { return loadImage(sized(q[0], Math.max(xs, W - xs))); })).then(function (im) {
          var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
          var g = cv.getContext('2d'); g.imageSmoothingQuality = 'high';
          g.drawImage(im[0], 0, 0, xs, ys); g.drawImage(im[1], xs, 0, W - xs, ys);
          g.drawImage(im[2], 0, ys, xs, H - ys); g.drawImage(im[3], xs, ys, W - xs, H - ys);
          im.forEach(function (b) { if (b.close) b.close(); });
          return cv;
        }).catch(function () { c.quads = null; return loadImage(sized(c.img, longW(c, Math.min(TEX_LONG, longOf(c.w, c.h) || TEX_LONG)))); });
      }
      return loadImage(sized(c.img, longW(c, Math.min(TEX_LONG, longOf(c.w, c.h) || TEX_LONG))));
    }
    function greyOf(img, w) {
      var h = Math.max(1, Math.round(w * img.height / img.width)), src = img, sw = img.width, sh = img.height;
      while (sw > 2 * w) {                               // halve step by step: one big jump aliases and spoils the match
        var nw = Math.max(w, Math.round(sw / 2)), nh = Math.max(h, Math.round(sh / 2)), t = document.createElement('canvas');
        t.width = nw; t.height = nh; var tg = t.getContext('2d'); tg.imageSmoothingQuality = 'high'; tg.drawImage(src, 0, 0, nw, nh);
        src = t; sw = nw; sh = nh;
      }
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      var g = cv.getContext('2d', { willReadFrequently: true }); g.imageSmoothingQuality = 'high'; g.drawImage(src, 0, 0, w, h);
      var d = g.getImageData(0, 0, w, h).data, a = new Float32Array(w * h);
      for (var k = 0; k < w * h; k++) a[k] = 0.299 * d[4 * k] + 0.587 * d[4 * k + 1] + 0.114 * d[4 * k + 2];
      return { a: a, w: w, h: h };
    }
    function ncc(F, ox, oy, T) {
      var sm = 0, s2 = 0, tm = 0, t2 = 0, cr = 0, n = 0;
      for (var y = 0; y < T.h && oy + y < F.h; y++) for (var x = 0; x < T.w && ox + x < F.w; x++) {
        var v = F.a[(oy + y) * F.w + ox + x], t = T.a[y * T.w + x];
        sm += v; s2 += v * v; tm += t; t2 += t * t; cr += v * t; n++;
      }
      if (n < 16) return -1;
      var cov = cr - sm * tm / n, fv = s2 - sm * sm / n, tv = t2 - tm * tm / n;
      return cov / Math.sqrt(fv * tv + 1e-6);
    }
    // are the product's next four images the four quarters of the sheet? (in any order)
    function findQuads(c, flatImg) {
      var Q = (c.q || []).filter(function (q) { return Math.abs(q[1] / q[2] - c.a) < 0.012 && longOf(q[1], q[2]) > 0.6 * longOf(c.w, c.h); }).slice(0, 4);
      if (Q.length < 4 || HI !== 'lg') return Promise.resolve(null);
      var FW = 128, F = greyOf(flatImg, FW), tw = FW / 2, spots = [[0, 0], [0.5, 0], [0, 0.5], [0.5, 0.5]];
      return Promise.all(Q.map(function (q) { return loadImage(sized(q[0], tw)); })).then(function (im) {
        var Ts = im.map(function (b) { var g = greyOf(b, tw); if (b.close) b.close(); return g; });
        var M = Ts.map(function (T) { return spots.map(function (sp) { return ncc(F, Math.round(sp[0] * F.w), Math.round(sp[1] * F.h), T); }); });
        var best = null, perm = [0, 1, 2, 3];
        (function permute(k) {
          if (k === 4) {
            var lo = Math.min(M[perm[0]][0], M[perm[1]][1], M[perm[2]][2], M[perm[3]][3]);
            if (!best || lo > best.lo) best = { lo: lo, p: perm.slice() };
            return;
          }
          for (var j = k; j < 4; j++) { var t = perm[k]; perm[k] = perm[j]; perm[j] = t; permute(k + 1); t = perm[k]; perm[k] = perm[j]; perm[j] = t; }
        })(0);
        return best && best.lo > 0.75 ? best.p.map(function (j) { return Q[j]; }) : null;     // quarters score ≈ .9–1, other crops < .3
      }).catch(function () { return null; });
    }
    // layout analysis, off the main thread when possible
    var worker = null, wq = {}, wid = 0;
    function analyse(img) {
      var sc = Math.min(1, LONG_SM / Math.max(img.width, img.height));
      var w = Math.max(8, Math.round(img.width * sc)), h = Math.max(8, Math.round(img.height * sc));
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      var g = cv.getContext('2d', { willReadFrequently: true }); g.drawImage(img, 0, 0, w, h);
      var d = g.getImageData(0, 0, w, h).data;
      if (worker === null) {
        try {
          worker = new Worker(URL.createObjectURL(new Blob(['var tbLayout=' + tbLayout.toString() +
            ';onmessage=function(e){postMessage({id:e.data.id,r:tbLayout(e.data.d,e.data.w,e.data.h)});};'], { type: 'text/javascript' })));
          worker.onmessage = function (ev) { var j = wq[ev.data.id]; delete wq[ev.data.id]; if (j) j.res(ev.data.r); };
          worker.onerror = function () {        // no workers here: finish the pending ones on this thread
            worker = false;
            Object.keys(wq).forEach(function (id) { var j = wq[id]; delete wq[id]; j.res(tbLayout(j.d, j.w, j.h)); });
          };
        } catch (err) { worker = false; }
      }
      return new Promise(function (res) {
        if (worker) { var id = ++wid; wq[id] = { res: res, d: d, w: w, h: h }; worker.postMessage({ id: id, d: d, w: w, h: h }); }
        else setTimeout(function () { res(tbLayout(d, w, h)); }, 0);
      });
    }
    function prepare(i) {
      var c = DATA[i], e = assets(i);
      if (!c.dyn) return Promise.resolve(c);
      if (!e.prep) {
        e.prep = e.sm.then(function (img) {
          return Promise.all([analyse(img), findQuads(c, img)]).then(function (r) {
            var Lo = r[0];
            c.box = Lo.box; c.poi = Lo.poi; if (Lo.tit) c.tit = Lo.tit;
            c.quads = r[1];
            var texLong = c.quads ? Math.min(TEX_LONG, 2 * longOf(c.quads[0][1], c.quads[0][2])) : Math.min(TEX_LONG, longOf(c.w, c.h) || TEX_LONG);
            c.kD = clamp(Math.pow((HI === 'lg' ? 4096 : 2048) / texLong, 0.7), 1, 1.55);
            return c;
          });
        }).catch(function () { c.bad = true; throw new Error('map ' + c.id); });
      }
      // A variant may return with a new record object and an already prepared cache.
      // Reuse its analysis on that record instead of recursively preparing forever.
      return e.prep.then(function (prepared) {
        if (prepared !== c) {
          c.box = prepared.box; c.poi = prepared.poi; c.tit = prepared.tit;
          c.quads = prepared.quads; c.kD = prepared.kD;
        }
        return c;
      });
    }

    // ---------------------------------------------------------------- card
    function fmtEle(m) { return String(m).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' m'; }
    function searchUrl(c) { return SITE + '/search?q=' + encodeURIComponent(typeof c.q === 'string' ? c.q : c.name) + '&type=product'; }
    function productUrl(c) {
      if (c.dyn) return c.url || searchUrl(c);               // shop maps: the product's own (localised) URL
      if (c.url) return SITE + c.url;                         // 100 Cims prints: one product each
      return c.h ? SITE + '/products/' + c.h : searchUrl(c);  // other maps: the product, or every style of that place
    }
    // on the shop itself, make sure a product still exists before sending people to it
    var checked = {};
    function checkLink(c) {
      if (c.dyn || !c.h || !window.fetch || /^https?:/i.test(SITE)) return;
      if (checked[c.h] === undefined) {
        checked[c.h] = fetch(SITE + '/products/' + c.h + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok; }).catch(function () { return true; });
      }
      checked[c.h].then(function (ok) { if (!ok && DATA[thumbCim] === c) elLink.href = searchUrl(c); });
    }
    function setCard(i, animate) {
      root.dispatchEvent(new CustomEvent('trobart:map-change', { detail: { id: String(DATA[i].pid || DATA[i].id), variant: DATA[i].vid } }));
      var c = DATA[i];
      var apply = function () {
        if (c.poi) {                                          // a Trobart map
          elNum.textContent = c.kind || 'Mapa';
          elEss.textContent = c.style || ''; elEss.hidden = !c.style;
          elMeta.hidden = true; elTag.hidden = true;
        } else {                                              // a 100 Cims print
          elNum.textContent = String(c.n).padStart(3, '0') + '/522';
          elEss.hidden = !c.ess;
          elMeta.textContent = fmtEle(c.ele) + ' · ' + c.com.join(' · ');
          elTag.textContent = c.tag || '';
        }
        elName.textContent = c.name;
        elLink.href = productUrl(c);
        elLink.setAttribute('aria-label', (c.poi ? txt('Veure el mapa de ', 'Ver el mapa de ') : txt('Veure la làmina de ', 'Ver la lámina de ')) + c.name);
        checkLink(c);
        thumbCim = i;
        var a = cache[c.id];
        if (a && a.smImg) thumbImg = a.smImg;
        if (!gl) drawThumb(null);
        layoutVars();
      };
      if (animate && !reduceMotion) {
        card.classList.remove('is-turning'); void card.offsetWidth; card.classList.add('is-turning');
        setTimeout(apply, 300);
      } else apply();
    }
    function layoutVars() {
      root.style.setProperty('--tb-card-h', card.offsetHeight + 'px');
      var headline = $('.tb-intro');
      if (!headline || !root.classList.contains('tb-hero--trobart') || root.classList.contains('tb-ui-hidden')) return;
      var inset = parseFloat(getComputedStyle(headline).top) || 16;
      var lowerHeight = card.offsetHeight + 6;
      var controls = $('.tb-controls');
      if (root.clientWidth <= 760 && controls) lowerHeight += controls.offsetHeight + 9;
      var minimum = Math.ceil(headline.offsetHeight + lowerHeight + 2 * inset + 32);
      root.style.setProperty('--tb-content-min-h', minimum + 'px');
    }

    // ---------------------------------------------------------------- thumbnail with viewfinder
    var thumbImg = null, thumbCim = -1;
    var thumbStroke = (getComputedStyle(root).getPropertyValue('--tb-viewfinder') || '').trim() || '#d9a945';
    function drawThumb(layer) {
      var W = thumb.width, H = thumb.height;
      tctx.fillStyle = '#e9e6de'; tctx.fillRect(0, 0, W, H);
      if (thumbImg) {
        var th = Math.round(W / DATA[thumbCim].a);
        if (thumb.height !== th) { thumb.height = th; H = th; }
        tctx.drawImage(thumbImg, 0, 0, W, H);
      }
      if (!layer || !layer.cam || layer.cim !== thumbCim) return;
      var S = layer.S, asp = canvas.width / Math.max(1, canvas.height), pts = [], k;
      var edge = function (x, y) { var h = hit(layer.cam, x, y, asp); pts.push([h.x / S[0] * W, h.y / S[1] * H]); };
      for (k = 0; k <= 8; k++) edge(-1 + k / 4, -1);
      for (k = 1; k <= 8; k++) edge(1, -1 + k / 4);
      for (k = 1; k <= 8; k++) edge(1 - k / 4, 1);
      for (k = 1; k < 8; k++) edge(-1, 1 - k / 4);
      tctx.save();
      tctx.beginPath(); tctx.rect(0, 0, W, H);
      tctx.moveTo(pts[0][0], pts[0][1]);
      for (k = 1; k < pts.length; k++) tctx.lineTo(pts[k][0], pts[k][1]);
      tctx.closePath();
      tctx.fillStyle = 'rgba(20,23,21,0.38)'; tctx.fill('evenodd');
      tctx.beginPath(); tctx.moveTo(pts[0][0], pts[0][1]);
      for (k = 1; k < pts.length; k++) tctx.lineTo(pts[k][0], pts[k][1]);
      tctx.closePath(); tctx.lineWidth = 2; tctx.strokeStyle = thumbStroke; tctx.stroke();
      tctx.restore();
    }

    // ---------------------------------------------------------------- shot planning
    function clampMap(c, u, v) {
      var M = c.map;
      return [clamp(u, M[0] + 0.07, M[2] - 0.07), clamp(v, M[1] + 0.06, M[3] - 0.08)];
    }
    // move a close view's target so the frame stays on the sheet rather than showing the desk
    function onSheet(p, S2) {
      var asp = canvas.width / Math.max(1, canvas.height), d = Math.exp(p.ld) * Math.tan(p.fov * DEG / 2) * 1.04;
      var hy = d / S2[1], hx = d * asp / S2[0];
      p.u = hx < 0.5 ? clamp(p.u, hx, 1 - hx) : 0.5;
      p.v = hy < 0.5 ? clamp(p.v, hy, 1 - hy) : 0.5;
      return p;
    }
    // open at the sheet's centre, then tour its details, printed name or the whole sheet
    function makePlanMap(i, r, from) {
      var c = DATA[i], keys = [], t = 0, S2 = sheetSize(c), Bx = c.box || [0, 0, 1, 1];
      var P = c.poi.map(function (p) {                    // keep the camera's targets a little inside the artwork
        return [clamp(p[0], Bx[0] + 0.07, Bx[2] - 0.07), clamp(p[1], Bx[1] + 0.07, Bx[3] - 0.07)];
      });
      var dist = function (p, q) { return Math.hypot((p[0] - q[0]) * S2[0], (p[1] - q[1]) * S2[1]) / Math.max(S2[0], S2[1]); };
      var A = P[Math.floor(r() * Math.min(4, P.length))];
      var rest = P.filter(function (p) { return p !== A; });
      rest.sort(function (p, q) { return Math.abs(dist(p, A) - 0.3) - Math.abs(dist(q, A) - 0.3); });
      var Bp = rest.length ? rest[Math.floor(r() * Math.min(2, rest.length))] : [lerp(A[0], 0.5, 0.5), lerp(A[1], 0.5, 0.5)];
      var kD = closeupScale(c);                         // portrait baseline, with image-quality protection
      if (from) { keys.push({ t: 0, p: copyPose(from) }); t = 0; }
      else { keys.push({ t: 0, p: onSheet(pose(0.5, 0.5, (13 + r() * 5) * kD, 13 + r() * 7, azPick(r), 40), S2) }); t = 8.5; }
      keys.push({ t: t, p: onSheet(pose(Bp[0], Bp[1], (12 + r() * 4) * kD, 14 + r() * 6, azPick(r), 40), S2) });
      var f = r(), last, tanY = Math.tan(20 * DEG);
      if (c.tit && f < 0.5) {         // read the map's name, on the sheet or in its label box
        var T = c.tit, wUnits = Math.max(T[3] * S2[0], (T[2] - T[1]) * S2[1] * 4);
        var d = clamp(wUnits / 0.55 / (2 * tanY * 1.6), 6, 34);
        last = pose(T[0], (T[1] + T[2]) / 2, Math.max(d, 7 * kD), 8 + r() * 5, azPick(r, 0.6), 38);
        t += 8;
      } else if (f < 0.82 || !rest[1]) {   // pull back: the whole sheet on the desk
        var dw = Math.max(S2[1], S2[0] / 1.6) / 2 / tanY * 1.12;
        last = pose(0.5, 0.5, dw, 5 + r() * 5, azPick(r, 0.6), 40);
        t += 8.5;
      } else {
        var Cp = rest[rest.length - 1];
        last = onSheet(pose(Cp[0], Cp[1], (11 + r() * 4) * kD, 12 + r() * 7, azPick(r), 40), S2);
        t += 8.5;
      }
      keys.push({ t: t, p: last });
      return { keys: keys, end: t + 1.6 };
    }
    function sheetSize(c) {
      if (!c.poi) return [PRINT_H * c.a, PRINT_H];
      var L = 44;                                             // other maps: 44 units on the long side
      return c.a >= 1 ? [L, L / c.a] : [L * c.a, L];
    }
    // Give horizontal artwork the portrait close-up baseline plus a safety margin.
    // Keep the existing portrait scale; a 10:7 landscape backs off by 10/7.
    function closeupScale(c) {
      return Math.max(1, c.a || 1) * (c.kD || 1);
    }
    function mapZoomDistance(c, fov) {
      var d = 12 * DSCALE * closeupScale(c);
      // Title shots use a narrower lens. Keep their landscape magnification capped too.
      return c.a > 1 && fov ? d * Math.tan(20 * LENS * DEG) / Math.tan(fov * DEG / 2) : d;
    }
    // open at the sheet's centre, then visit the summit label, title, number or whole sheet
    function makePlan(i, r, from) {
      if (DATA[i].poi) return makePlanMap(i, r, from);
      var c = DATA[i], S = c.sum, keys = [];
      var side = r() < 0.5 ? -1 : 1;
      var a = r() * Math.PI * 2, rad = 0.12 + r() * 0.12;
      var p0 = clampMap(c, S[0] + Math.cos(a) * rad * 0.8, S[1] + Math.sin(a) * rad);
      var t = 0;
      if (from) { keys.push({ t: 0, p: copyPose(from) }); }
      else {
        keys.push({ t: 0, p: pose(0.5, 0.5, 13 + r() * 4, 13 + r() * 6, azPick(r), 40) });
        t = 7.5;
        var mid = clampMap(c, lerp(p0[0], S[0], 0.55) + side * 0.03, lerp(p0[1], S[1], 0.55));
        keys.push({ t: t, p: pose(mid[0], mid[1], 13 + r() * 3, 14 + r() * 6, azPick(r), 40) });
      }
      t += 6.5;
      keys.push({ t: t, p: pose(S[0] + 0.028, S[1] - 0.012, 11.5 + r() * 3, 12 + r() * 6, azPick(r), 38) });
      var f = r(), last;
      if (f < 0.55) {         // read the printed name, altitude and comarca
        var T = c.tit;
        last = pose(T[0], (T[1] + T[2]) / 2 - 0.004, 14.5 + r() * 2, 8 + r() * 5, azPick(r, 0.6), 38);
        t += 8;
      } else if (f < 0.8) {   // pull back: the whole sheet lying on the desk
        last = pose(0.5, 0.5, 64 + r() * 8, 5 + r() * 5, azPick(r, 0.6), 40);
        t += 8.5;
      } else {                // the golden collector's number in the corner
        last = pose(c.num[0] - 0.035, c.num[1] + 0.02, 8 + r() * 1.5, 12 + r() * 6, azPick(r), 38);
        t += 8.5;
      }
      keys.push({ t: t, p: last });
      return { keys: keys, end: t + 1.6 };
    }
    function planAt(plan, t, S) {
      var K = plan.keys, n = K.length;
      if (t <= K[0].t) return copyPose(K[0].p);
      if (t >= K[n - 1].t) return copyPose(K[n - 1].p);
      var j = 1; while (K[j].t < t) j++;
      var A = K[j - 1], B = K[j], s = (t - A.t) / (B.t - A.t), e = ease(s), out = {};
      for (var k = 0; k < KEYS.length; k++) out[KEYS[k]] = lerp(A.p[KEYS[k]], B.p[KEYS[k]], e);
      // rise a little while travelling between distant details
      var travel = Math.hypot((B.p.u - A.p.u) * S[0], (B.p.v - A.p.v) * S[1]);
      var view = 2 * Math.exp(Math.min(A.p.ld, B.p.ld)) * Math.tan(A.p.fov * DEG / 2);
      out.ld += 0.3 * Math.log(1 + travel / view) * Math.pow(Math.sin(Math.PI * s), 2);
      return out;
    }

    // ---------------------------------------------------------------- layers (one per print on screen)
    function Layer(i) {
      this.cim = i; this.c = DATA[i];
      this.S = sheetSize(this.c);
      this.tex = null; this.hiReady = false; this.lowReady = false;
      this.plan = null; this.t = 0;
      this.pose = null; this.vel = {}; this.goal = null;
      this.focus = 20; this.ap = 1; this.cam = null;
    }
    Layer.prototype.start = function (from) {
      this.plan = reduceMotion ? stillPlan(this.cim) : makePlan(this.cim, rng32((seed + this.cim * 7919) ^ (Date.now() & 0xffff)), from);
      this.t = 0;
      if (!from) {
        this.pose = copyPose(this.plan.keys[0].p);
        this.pose.ld += 0.18;            // start a little further away and settle in
      }
      for (var k = 0; k < KEYS.length; k++) this.vel[KEYS[k]] = 0;
      this.goal = copyPose(this.plan.keys[0].p);
      this.focus = Math.exp(this.pose.ld);
    };
    function stillPlan(i) {
      var c = DATA[i];
      if (c.poi) return { keys: [{ t: 0, p: pose(0.5, 0.5, 22 * closeupScale(c), 10, 0, 40) }], end: 14 };
      return { keys: [{ t: 0, p: pose(0.5, 0.5, 22, 10, 0, 40) }], end: 14 };
    }

    // ---------------------------------------------------------------- GL
    var prog, U = {}, quality = 1, taps = coarse ? 7 : 10;
    function compile(type, src) {
      var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }
    function setupGL() {
      prog = gl.createProgram();
      gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS));
      gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS));
      gl.bindAttribLocation(prog, 0, 'p');
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
      gl.useProgram(prog);
      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      ['uRes', 'uTime', 'uMix', 'uExpo', 'uTaps', 'uTex0', 'uTex1', 'uSize0', 'uSize1',
       'uC0', 'uF0', 'uR0', 'uU0', 'uC1', 'uF1', 'uR1', 'uU1', 'uL0', 'uL1', 'uDesk'].forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });
      gl.uniform1i(U.uTex0, 0); gl.uniform1i(U.uTex1, 1);
      // the desk colour comes from the stylesheet (--tb-desk-gl), squared because the shader works in gamma 2
      var dk = /#([0-9a-f]{6})/i.exec(getComputedStyle(root).getPropertyValue('--tb-desk-gl') || '');
      var dv = dk ? [0, 2, 4].map(function (k) { var v = parseInt(dk[1].substr(k, 2), 16) / 255; return v * v; }) : [0.030, 0.035, 0.033];
      gl.uniform3f(U.uDesk, dv[0], dv[1], dv[2]);
      aniso = gl.getExtension('EXT_texture_filter_anisotropic');
      blank = makeTex();
    }
    var aniso = null, blank = null;
    function makeTex() {
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([236, 233, 226, 255]));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      return t;
    }
    function upload(tex, img) {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      if (aniso) gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(12, gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
    }
    // give a layer its texture: the small image at once, the full one when it arrives
    function feed(layer) {
      var e = assets(layer.cim);
      layer.tex = makeTex();
      e.sm.then(function (img) {
        if (!layer.hiReady && !layer.dead) { upload(layer.tex, img); layer.lowReady = true; }
      }).catch(function () {});
      hiImage(layer.cim).then(function (img) {
        if (layer.dead) return;
        upload(layer.tex, img); layer.hiReady = true;
        if (img.close) { img.close(); cache[layer.c.id].hi = null; }
        else if (img.getContext) { img.width = img.height = 0; cache[layer.c.id].hi = null; }
      }).catch(function () { layer.failed = true; });
    }
    function dropLayer(layer) {
      if (!layer) return;
      layer.dead = true;
      if (gl && layer.tex) gl.deleteTexture(layer.tex);
      layer.tex = null;
    }

    // ---------------------------------------------------------------- state
    var A = null, B = null, mix = 0, trans = null, intro = null;
    var mode = 'auto', paused = false, lastInput = -1e9, now = 0;
    var hover = [0, 0], hoverS = [0, 0];
    var running = true, visible = true;

    var pending = null;
    function show(i, opts) {
      var c = DATA[i];
      if (c.bad) {
        if (DATA.every(function (m) { return m.bad; })) {
          loading.textContent = txt('No hem pogut carregar els mapes. Explora el catàleg.', 'No hemos podido cargar los mapas. Explora el catálogo.');
          $('.tb-controls').hidden = true; return;
        }
        if (cataloguePicker) requestCatalogueMap(); else show(stepOrder(1), opts);
        return;
      }
      if (c.dyn && !c.poi) {
        pending = c;
        prepare(i).then(function () { if (pending === c) { pending = null; show(i, opts); } })
          .catch(function () { if (pending === c) { pending = null; if (cataloguePicker) requestCatalogueMap(); else if (DATA.length > 1) show(stepOrder(1), opts); } });
        return;
      }
      pending = null;
      opts = opts || {};
      var L = new Layer(i);
      L.start(null);
      if (gl) feed(L); else assets(i);
      if (!B) {                        // first print: fade in from a blur once the image is ready
        B = L; mix = 1; L.ap = reduceMotion ? 1 : 9;
        intro = { t: 0, ready: false };
        setCard(i, false);
      } else {
        if (trans) { dropLayer(A); }   // interrupted: the one coming in becomes the outgoing one
        A = B; B = L; mix = 0;
        trans = { t: 0, swapped: false, wait: 0, a0: A.ap };
        if (intro) { intro = null; loading.classList.add('is-gone'); }
        L.ap = 8;
      }
      mode = 'auto';
      // warm the next one, let go of other prefetched full-size images
      var nx = peekNext(); assets(nx); prepare(nx).catch(function () {});
      Object.keys(cache).forEach(function (id) {
        var e = cache[id];
        if (e.hi && id !== String(DATA[i].id) && id !== String(DATA[nx].id) && !(A && id === String(A.c.id))) {
          e.hi.then(function (b) { if (b && b.close) b.close(); else if (b && b.getContext) b.width = b.height = 0; }).catch(function () {}); e.hi = null;
        }
      });
      setTimeout(function () { hiImage(nx).catch(function () {}); }, 2500);
      if (!gl) showFallback(i);
    }
    // Homepage navigation samples collections, even after its image cache grows.
    // Product viewers keep their own order and one-finger panning.
    var cataloguePicker = null, cataloguePicking = false, catalogueRetryAt = 0;
    function requestCatalogueMap() {
      if (!cataloguePicker) return false;
      if (cataloguePicking || Date.now() < catalogueRetryAt) return true;
      cataloguePicking = true;
      Promise.resolve().then(cataloguePicker).then(function (item) {
        if (!item) { catalogueRetryAt = Date.now() + 2000; return; }
        var id = String(item.id), key = id + ':' + (item.vid || ''),
            i = DATA.findIndex(function (c) { return String(c.id) === key; });
        if (i < 0) {
          i = DATA.length;
          DATA.push(normalizeMap(Object.assign({}, item, { pid: id, id: key })));
          order.push(i);
        }
        pos = order.indexOf(i);
        root.dataset.catalogueCollection = item.collection;
        show(i);
      }).catch(function () { catalogueRetryAt = Date.now() + 2000; })
        .finally(function () { cataloguePicking = false; });
      return true;
    }
    function goNext() { if (!requestCatalogueMap()) show(stepOrder(1)); }
    function goPrev() { if (!requestCatalogueMap()) show(stepOrder(-1)); }
    function goRandom() { if (!requestCatalogueMap()) show(randomPick()); }

    // ---------------------------------------------------------------- per-frame update
    var T_OUT = 0.9, T_MIX0 = 0.45, T_MIX1 = 1.2, T_IN = 2.2;
    function stepLayer(L, dt, active) {
      if (!L || !L.plan) return;
      var auto = !active || mode === 'auto';
      if (auto && !(active && paused) && !(intro && !intro.ready)) L.t += dt;
      if (auto) {
        var g = planAt(L.plan, L.t, L.S);
        for (var k = 0; k < KEYS.length; k++) L.goal[KEYS[k]] = g[KEYS[k]];
      }
      if (L.c.a > 1) L.goal.ld = Math.max(L.goal.ld, Math.log(mapZoomDistance(L.c, L.goal.fov)));
      var omega = (active && mode === 'user') ? 7 : 1.25;
      for (var k2 = 0; k2 < KEYS.length; k2++) {
        var key = KEYS[k2], r = spring(L.pose[key], L.vel[key], L.goal[key], omega, dt);
        L.pose[key] = r[0]; L.vel[key] = r[1];
      }
      // render pose: add a slow handheld float and the mouse parallax
      var rp = copyPose(L.pose), roll = 0;
      if (!reduceMotion) {
        rp.az += 0.35 * Math.sin(now * 0.21 + 1.3) + 0.15 * Math.sin(now * 0.53);
        rp.tilt += 0.3 * Math.sin(now * 0.17 + 0.4) + 0.1 * Math.sin(now * 0.61 + 2);
        roll = 0.25 * Math.sin(now * 0.13 + 0.7);
        rp.ld += 0.006 * Math.sin(now * 0.29);
        if (active) { rp.az += hoverS[0] * 3; rp.tilt -= hoverS[1] * 2; }
      }
      rp.tilt = clamp(rp.tilt, 3, 60);
      if (L.c.a > 1) rp.ld = Math.max(rp.ld, Math.log(mapZoomDistance(L.c, rp.fov)));
      L.cam = camera(rp, L.S, roll);
      // Keep a short cinematic focus transition while responding quickly to exploration.
      var fd = L.cam.d, k3 = 1 - Math.exp(-dt / 0.15);
      L.focus = Math.exp(Math.log(L.focus) + k3 * (Math.log(fd) - Math.log(L.focus)));
    }
    function update(dt) {
      hoverS[0] += (hover[0] - hoverS[0]) * (1 - Math.exp(-dt * 2));
      hoverS[1] += (hover[1] - hoverS[1]) * (1 - Math.exp(-dt * 2));
      if (intro) {
        if (!intro.ready && B && (B.hiReady || (B.lowReady && intro.t > 3.5) || B.failed)) {
          intro.ready = true; intro.t = 0; fallback.hidden = true; loading.classList.add('is-gone');
        }
        intro.t += dt;
        if (intro.ready) {
          B.ap = reduceMotion ? 1 : lerp(9, 1, smooth(intro.t / 2.4));
          if (intro.t > 2.4) { B.ap = 1; intro = null; }
        }
      }
      if (trans) {
        // wait (blurred) for the new print to have at least its small image
        if (!B.lowReady && !B.hiReady && trans.wait < 2.5 && trans.t >= T_MIX0) { trans.wait += dt; }
        else trans.t += dt;
        var t = trans.t;
        if (A) A.ap = lerp(trans.a0, Math.max(7, trans.a0), smooth(t / T_OUT));
        mix = smooth((t - T_MIX0) / (T_MIX1 - T_MIX0));
        B.ap = t < T_MIX1 ? 8 : lerp(8, 1, smooth((t - T_MIX1) / (T_IN - T_MIX1)));
        if (!trans.swapped && mix > 0.5) { trans.swapped = true; setCard(B.cim, true); }
        if (t >= T_IN + 0.1) { B.ap = 1; dropLayer(A); A = null; mix = 1; trans = null; }
      }
      stepLayer(A, dt, false);
      stepLayer(B, dt, !trans || mix > 0.5);
      // autopilot hand-back and the end of a shot
      if (mode === 'user' && !paused && now - lastInput > IDLE_RESUME_S && B) {
        mode = 'auto';
        var from = copyPose(B.pose);
        B.plan = resumePlan(B.cim, from); B.t = 0;
      }
      if (B && B.plan && mode === 'auto' && !paused && !trans && !intro && pending === null && B.t >= B.plan.end && !reduceMotion) goNext();
      if (B && B.failed && !trans && !intro) { B.failed = false; goNext(); }
      // progress
      if (B && B.plan) {
        bar.style.width = (100 * clamp(B.t / B.plan.end, 0, 1)).toFixed(2) + '%';
        barWrap.classList.toggle('is-held', paused || mode === 'user');
      }
    }
    function resumePlan(i, from) {
      var p = makePlan(i, rng32(Date.now() & 0xffffff), from);
      // keep only the start and the finale, a little quicker
      var K = p.keys, last = K[K.length - 1];
      var travel = Math.hypot(last.p.u - from.u, last.p.v - from.v);
      var dur = 4 + 10 * clamp(travel, 0, 0.6);
      return { keys: [{ t: 0, p: copyPose(from) }, { t: dur, p: last.p }], end: dur + 1.6 };
    }

    // ---------------------------------------------------------------- render
    function setLayer(L, n) {
      var cam = L.cam, asp = canvas.width / Math.max(1, canvas.height);
      gl.activeTexture(n ? gl.TEXTURE1 : gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, L.tex || blank);
      gl.uniform2f(U['uSize' + n], L.S[0], L.S[1]);
      gl.uniform3fv(U['uC' + n], cam.C); gl.uniform3fv(U['uF' + n], cam.F);
      gl.uniform3fv(U['uR' + n], cam.R); gl.uniform3fv(U['uU' + n], cam.U);
      gl.uniform4f(U['uL' + n], cam.tanY, asp, L.focus, BASE_APERTURE * L.ap);
    }
    function render() {
      if (!B || !B.cam) return;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(U.uRes, canvas.width, canvas.height);
      gl.uniform1f(U.uTime, now % 1000);
      gl.uniform1i(U.uTaps, taps);
      var expo = 1;
      if (trans) expo = 1 - 0.08 * Math.sin(Math.PI * clamp(trans.t / T_IN, 0, 1));
      if (intro && !intro.ready) expo = 0.9;
      gl.uniform1f(U.uExpo, expo);
      setLayer(A || B, 0); setLayer(B, 1);
      gl.uniform1f(U.uMix, A ? mix : 1);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    // ---------------------------------------------------------------- sizing and adaptive quality
    var dpr = 1;
    function resize() {
      var r = root.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, coarse ? 3 : 1.5);
      dpr = Math.min(dpr, Math.sqrt(3.6e6 / Math.max(1, r.width * r.height))) * quality;   // keep the pixel count sane on big screens
      var w = Math.max(2, Math.round(r.width * dpr)), h = Math.max(2, Math.round(r.height * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      var tw = Math.round(thumb.getBoundingClientRect().width * Math.min(2, window.devicePixelRatio || 1)) || 184;
      if (thumb.width !== tw) thumb.width = tw;
      layoutVars();
    }
    var ft = 16, slow = 0, fast = 0;
    var fixedQ = 0;
    function adapt(dtms) {
      if (fixedQ) return;
      ft = ft * 0.92 + dtms * 0.08;
      if (ft > 27) { slow += dtms; fast = 0; } else if (ft < 20) { fast += dtms; slow = 0; } else { slow = fast = 0; }
      if (slow > 1500 && quality > 0.5) { quality = Math.max(0.5, quality * 0.85); if (quality < 0.75) taps = Math.max(6, taps - 2); slow = 0; resize(); }
      if (fast > 5000 && quality < 1) { quality = Math.min(1, quality * 1.1); fast = 0; resize(); }
    }

    // ---------------------------------------------------------------- input
    var ptrs = new Map(), drag = null, gesture = null, swipe = null;
    function touchInput() {
      lastInput = now;
      if (mode !== 'user') { mode = 'user'; if (B) B.goal = copyPose(B.pose); }
      hint.classList.add('is-gone');
    }
    function ndc(e) {
      var r = canvas.getBoundingClientRect();
      return [((e.clientX - r.left) / r.width) * 2 - 1, 1 - ((e.clientY - r.top) / r.height) * 2];
    }
    function panBy(dxCss, dyCss) {
      if (!B || !B.cam) return;
      var g = B.goal, rect = canvas.getBoundingClientRect();
      var fpx = (rect.height / 2) / Math.tan(g.fov * DEG / 2), s = Math.exp(g.ld) / fpx;
      var cam = B.cam, Rg = [cam.R[0], cam.R[1]], Rl = Math.hypot(Rg[0], Rg[1]) || 1;
      var dir = cam.dir, ct = Math.max(0.35, Math.cos(g.tilt * DEG));
      var dx = -s * (dxCss * Rg[0] / Rl + dyCss / ct * dir[0]);
      var dy = -s * (dxCss * Rg[1] / Rl + dyCss / ct * dir[1]);
      g.u = clamp(g.u + dx / B.S[0], 0.03, 0.97);
      g.v = clamp(g.v + dy / B.S[1], 0.03, 0.97);
    }
    // Keep manual close-ups within the available texture detail.
    function minZoomDistance() {
      return B ? mapZoomDistance(B.c, B.goal.fov) : 12 * DSCALE;
    }
    function zoomBy(f, at) {
      if (!B || !B.cam) return;
      var g = B.goal, d0 = Math.exp(g.ld), d1 = clamp(d0 * f, minZoomDistance(), 90 * DSCALE);
      if (at) {
        var h = hit(B.cam, at[0], at[1], canvas.width / canvas.height);
        var k = (1 - d1 / d0) * 0.85;
        g.u = clamp(g.u + (h.x / B.S[0] - g.u) * k, 0.03, 0.97);
        g.v = clamp(g.v + (h.y / B.S[1] - g.v) * k, 0.03, 0.97);
      }
      g.ld = Math.log(d1);
    }
    function rotateBy(daz, dtilt) {
      if (!B) return;
      B.goal.az += daz; B.goal.tilt = clamp(B.goal.tilt + dtilt, 4, 50);
    }
    canvas.addEventListener('pointerdown', function (e) {
      if (!B) return;
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (e.pointerType === 'touch') {
        if (SINGLE_TOUCH) { canvas.setPointerCapture(e.pointerId); touchInput(); }
        else if (ptrs.size === 1) swipe = { x: e.clientX, y: e.clientY, t: now };
        if (ptrs.size === 2) { swipe = null; gesture = twoFinger(); touchInput(); }
        return;
      }
      canvas.setPointerCapture(e.pointerId);
      drag = { x: e.clientX, y: e.clientY, rot: e.button === 2 || e.shiftKey };
      canvas.classList.add('is-dragging');
      touchInput();
    });
    canvas.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    function twoFinger() {
      var p = Array.from(ptrs.values());
      return { mx: (p[0].x + p[1].x) / 2, my: (p[0].y + p[1].y) / 2, d: Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y),
               a: Math.atan2(p[1].y - p[0].y, p[1].x - p[0].x) };
    }
    canvas.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'mouse' && !drag) {
        var n = ndc(e); hover[0] = clamp(n[0], -1, 1); hover[1] = clamp(n[1], -1, 1);
      }
      if (!ptrs.has(e.pointerId)) return;
      var pr = ptrs.get(e.pointerId), touchDx = e.clientX - pr.x, touchDy = e.clientY - pr.y;
      pr.x = e.clientX; pr.y = e.clientY;
      if (e.pointerType === 'touch') {
        if (SINGLE_TOUCH && ptrs.size === 1) {
          panBy(touchDx, touchDy); touchInput();
        } else if (ptrs.size === 2 && gesture) {
          var g2 = twoFinger();
          panBy(g2.mx - gesture.mx, g2.my - gesture.my);
          if (gesture.d > 10 && g2.d > 10) zoomBy(gesture.d / g2.d, null);
          var da = g2.a - gesture.a; if (da > Math.PI) da -= 2 * Math.PI; if (da < -Math.PI) da += 2 * Math.PI;
          // Screen-space clockwise twists increase the map’s clockwise rotation.
          rotateBy(da / DEG, 0);
          gesture = g2; touchInput();
        }
        return;
      }
      if (!drag) return;
      var dx = e.clientX - drag.x, dy = e.clientY - drag.y; drag.x = e.clientX; drag.y = e.clientY;
      if (drag.rot || e.shiftKey) rotateBy(dx * 0.25, -dy * 0.15); else panBy(dx, dy);
      touchInput();
    });
    function endPtr(e) {
      if (!SINGLE_TOUCH && e.pointerType === 'touch' && swipe && ptrs.size === 1 && e.type === 'pointerup') {
        var dx = e.clientX - swipe.x, dy = e.clientY - swipe.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > 2 * Math.abs(dy) && now - swipe.t < 0.8) { dx < 0 ? goNext() : goPrev(); hint.classList.add('is-gone'); }
      }
      ptrs.delete(e.pointerId);
      if (ptrs.size < 2) gesture = null;
      if (ptrs.size === 0) swipe = null;
      if (drag) { drag = null; canvas.classList.remove('is-dragging'); }
    }
    canvas.addEventListener('pointerup', endPtr);
    canvas.addEventListener('pointercancel', endPtr);
    canvas.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { hover[0] = hover[1] = 0; } });
    var wheelNote = 0;
    canvas.addEventListener('wheel', function (e) {
      if (!WHEEL_FREE && !e.ctrlKey && !e.metaKey) {
        if (now - wheelNote > 4) { wheelNote = now; hint.classList.remove('is-gone'); setTimeout(function () { hint.classList.add('is-gone'); }, 2500); }
        return;                         // let the page scroll
      }
      e.preventDefault();
      var dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      zoomBy(Math.exp(clamp(dy, -200, 200) * (e.ctrlKey ? 0.01 : 0.0016)), ndc(e));
      touchInput();
    }, { passive: false });
    canvas.addEventListener('dblclick', function (e) {
      zoomBy(0.6, ndc(e)); touchInput();
    });
    $('.tb-thumb').addEventListener('click', function (e) {
      if (!B || thumbCim !== B.cim) return;
      var r = thumb.getBoundingClientRect();
      touchInput();
      B.goal.u = clamp((e.clientX - r.left) / r.width, 0.03, 0.97);
      B.goal.v = clamp((e.clientY - r.top) / r.height, 0.03, 0.97);
      B.goal.ld = Math.log(clamp(Math.exp(B.goal.ld), minZoomDistance(), 26 * DSCALE));
    });
    $('.tb-next').addEventListener('click', function () { goNext(); });
    $('.tb-prev').addEventListener('click', function () { goPrev(); });
    $('.tb-shuffle').addEventListener('click', function () { goRandom(); });
    btnPause.addEventListener('click', function () { setPaused(!paused); });
    var toggleUI = $('.tb-toggle-ui');
    if (toggleUI) toggleUI.addEventListener('click', function () {
      var hidden = root.classList.toggle('tb-ui-hidden');
      toggleUI.setAttribute('aria-pressed', String(hidden));
      var label = hidden ? txt('Mostra la informació', 'Muestra la información') : txt('Amaga la informació', 'Oculta la información');
      toggleUI.setAttribute('aria-label', label); toggleUI.title = label;
      toggleUI.querySelector('.tb-eye-slash').toggleAttribute('hidden', !hidden);
      $('.tb-card').inert = hidden;
      if ($('.tb-intro')) $('.tb-intro').inert = hidden;
      var picker = $('.tb-map-picker'); if (picker) picker.inert = hidden;
      if (!hidden) layoutVars();
    });
    function setPaused(p) {
      paused = p;
      fallback.style.animationPlayState = p ? 'paused' : 'running';
      if (!gl) { clearTimeout(showFallback.t); if (!p && !reduceMotion) showFallback.t = setTimeout(goNext, 14000); }
      btnPause.setAttribute('aria-pressed', String(p));
      btnPause.setAttribute('aria-label', p ? txt('Continua el passeig', 'Continúa el paseo') : txt('Atura el passeig', 'Pausa el paseo'));
      btnPause.querySelector('.tb-i-pause').toggleAttribute('hidden', p);
      btnPause.querySelector('.tb-i-play').toggleAttribute('hidden', !p);
      if (!p && mode === 'user') lastInput = -1e9;
    }
    root.addEventListener('keydown', function (e) {
      if (e.target.closest && e.target.closest('input,select,textarea')) return;
      if (e.target.closest && e.target.closest('a,button') && (e.key === ' ' || e.key === 'Enter')) return;
      var k = e.key, used = true;
      if (k === 'ArrowRight') goNext();
      else if (k === 'ArrowLeft') goPrev();
      else if (k === ' ') setPaused(!paused);
      else if (k === '+' || k === '=') { zoomBy(0.8); touchInput(); }
      else if (k === '-' || k === '_') { zoomBy(1.25); touchInput(); }
      else used = false;
      if (used) e.preventDefault();
    });

    // ---------------------------------------------------------------- CSS fallback (no WebGL2)
    function showFallback(i) {
      var c = DATA[i];
      fallback.hidden = false;
      fallback.src = c.dyn ? sized(c.img, longW(c, 1600)) : BASE + c.img.md;
      fallback.alt = (c.poi ? txt('Detall del mapa de ', 'Detalle del mapa de ') : txt('Detall de la làmina de ', 'Detalle de la lámina de ')) + c.name;
      loading.classList.add('is-gone');
      setCard(i, true);
      clearTimeout(showFallback.t);
      if (!reduceMotion && !paused) showFallback.t = setTimeout(goNext, 14000);
    }

    // ---------------------------------------------------------------- loop
    var raf = 0, lastT = 0;
    function frame(ts) {
      raf = 0;
      if (!running) return;
      raf = requestAnimationFrame(frame);
      var dtms = lastT ? ts - lastT : 16; lastT = ts;
      var dt = Math.min(0.05, dtms / 1000);
      now += dt;
      update(dt);
      render();
      drawThumb(B && (!trans || mix > 0.5) ? B : A);
      adapt(dtms);
    }
    function setRunning(r) {
      running = r && visible && !document.hidden;
      if (running && !raf) { lastT = 0; raf = requestAnimationFrame(frame); }
    }

    function addMapRecords(items) {
      var known = new Set(DATA.map(function (c) { return String(c.pid || c.id); })), added = 0;
      items.forEach(function (c) {
        if (!c || known.has(String(c.id))) return;
        known.add(String(c.id)); order.push(DATA.length); DATA.push(normalizeMap(c)); added++;
      });
      if (added && root.classList.contains('tb-hero--trobart')) {
        // Preserve the current map and Back history; mix new maps into upcoming stops.
        var future = order.slice(pos + 1);
        for (var i = future.length - 1; i > 0; i--) { var j = Math.floor(rand() * (i + 1)), v = future[i]; future[i] = future[j]; future[j] = v; }
        order = order.slice(0, pos + 1).concat(future);
      }
    }

    // exposed for debugging and for the theme editor
    root.tbHero = {
      select: function (id) {
        var i = DATA.findIndex(function (c) { return String(c.id) === String(id); });
        if (i >= 0) { pos = order.indexOf(i); show(i); }
      },
      addMaps: addMapRecords,
      setMapPicker: function (picker) {
        if (root.classList.contains('tb-hero--trobart')) cataloguePicker = picker;
      },
      replaceMap: function (item) {
        var id = String(item.pid || item.id);
        var i = DATA.findIndex(function (c) { return String(c.pid || c.id) === id; });
        if (i < 0 || !item.vid) return false;
        // Keep one tour entry per product, with separate image caches for each size.
        var c = Object.assign({}, item, { pid: id, id: id + ':' + item.vid });
        DATA[i] = normalizeMap(c);
        pos = order.indexOf(i);
        show(i);
        return true;
      },
      next: goNext, prev: goPrev, random: goRandom, pause: setPaused,
      // run the clock forward without drawing (used for testing; images keep loading in real time)
      fixQuality: function (q) { fixedQ = q; quality = q; taps = q < 0.7 ? 7 : (coarse ? 7 : 10); resize(); },
      warp: function (sec) { for (var k = 0; k < Math.round(sec * 30); k++) { now += 1 / 30; update(1 / 30); } render(); drawThumb(B); },
      state: function () { return { cim: B && DATA[B.cim].id, t: B && B.t, mode: mode, quality: quality, tier: HI, mix: mix, trans: !!trans,
        pose: B && B.pose && { tilt: +B.pose.tilt.toFixed(1), az: +B.pose.az.toFixed(1), fov: +B.pose.fov.toFixed(1), d: +Math.exp(B.pose.ld).toFixed(1) }, kD: B && B.c.kD }; } };

    window.dispatchEvent(new CustomEvent('trobart:hero-ready'));

    // A product's viewer stays on the chosen map until the visitor starts the tour.
    if (startId && root.classList.contains('tb-hero--viewer')) setPaused(true);

    if (!gl) {
      root.classList.add('tb-nogl');
      show(order[0]);
      return;
    }
    try { setupGL(); } catch (err) {
      if (window.console) console.warn('[trobart-hero]', err);
      root.classList.add('tb-nogl'); gl = null; show(order[0]); return;
    }
    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault(); running = false; root.classList.add('tb-nogl'); gl = null; if (B) showFallback(B.cim);
    });
    resize();
    if (window.ResizeObserver) {
      var layoutObserver = new ResizeObserver(resize);
      layoutObserver.observe(root);
      if (root.classList.contains('tb-hero--trobart')) {
        layoutObserver.observe(card);
        var headline = $('.tb-intro');
        if (headline) layoutObserver.observe(headline);
      }
    } else window.addEventListener('resize', resize);
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (en) { visible = en[0].isIntersecting; setRunning(true); }).observe(root);
    }
    document.addEventListener('visibilitychange', function () { setRunning(true); });
    setTimeout(function () { hint.classList.add('is-gone'); }, 9000);
    show(order[0]);
    setRunning(true);

  }

  function boot() {
    var list = document.querySelectorAll('[data-tb-hero]');
    for (var i = 0; i < list.length; i++) if (!list[i].dataset.tbInitialized) init(list[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
